/**
 * API client for the internal AI assistant chat endpoint.
 *
 * POST /assistant/chat — sends a message, returns AI response.
 * POST /assistant/chat/stream — sends a message, returns SSE stream.
 */

import type { AssistantChatResponse } from "../types";

export interface SendChatMessageParams {
  message: string;
  sessionId: string;
  authToken: string | null;
}

export interface StreamCallbacks {
  onToken: (token: string) => void;
  onDone: (response: AssistantChatResponse) => void;
  onError: (error: Error) => void;
}

class AssistantApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "AssistantApiError";
  }
}

function getBaseUrl(): string {
  return import.meta.env.VITE_API_URL ?? "";
}

function buildHeaders(authToken: string | null): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (authToken) {
    headers["Authorization"] = `Bearer ${authToken}`;
  }

  return headers;
}

export async function sendChatMessage(
  params: SendChatMessageParams
): Promise<AssistantChatResponse> {
  const { message, sessionId, authToken } = params;

  const response = await fetch(`${getBaseUrl()}/assistant/chat`, {
    method: "POST",
    headers: buildHeaders(authToken),
    body: JSON.stringify({
      message,
      session_id: sessionId,
    }),
  });

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    throw new AssistantApiError(response.status, `Request failed with status ${response.status}`);
  }

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;

    if (data !== null && typeof data === "object" && "error" in data) {
      const errorObj = (data as { error: unknown }).error;
      if (errorObj !== null && typeof errorObj === "object" && "message" in errorObj) {
        const msg = (errorObj as { message: unknown }).message;
        if (typeof msg === "string") {
          errorMessage = msg;
        }
      }
    }

    throw new AssistantApiError(response.status, errorMessage);
  }

  if (
    data === null ||
    typeof data !== "object" ||
    !("data" in data) ||
    typeof (data as { data: unknown }).data !== "object" ||
    (data as { data: unknown }).data === null
  ) {
    throw new AssistantApiError(response.status, "Invalid response format from server");
  }

  const responseData = (data as { data: Record<string, unknown> }).data;

  if (
    typeof responseData.message !== "string" ||
    typeof responseData.session_id !== "string" ||
    typeof responseData.model_id !== "string" ||
    typeof responseData.model_name !== "string"
  ) {
    throw new AssistantApiError(response.status, "Invalid response format from server");
  }

  return responseData as unknown as AssistantChatResponse;
}

export async function streamChatMessage(
  params: SendChatMessageParams,
  callbacks: StreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const { message, sessionId, authToken } = params;

  const response = await fetch(`${getBaseUrl()}/assistant/chat/stream`, {
    method: "POST",
    headers: buildHeaders(authToken),
    body: JSON.stringify({
      message,
      session_id: sessionId,
    }),
    ...(signal ? { signal } : {}),
  });

  if (!response.ok) {
    let errorMessage = `Request failed with status ${response.status}`;

    try {
      const data: unknown = await response.json();
      if (data !== null && typeof data === "object" && "error" in data) {
        const errorObj = (data as { error: unknown }).error;
        if (errorObj !== null && typeof errorObj === "object" && "message" in errorObj) {
          const msg = (errorObj as { message: unknown }).message;
          if (typeof msg === "string") {
            errorMessage = msg;
          }
        }
      }
    } catch {
      // response may not be JSON for stream errors
    }

    throw new AssistantApiError(response.status, errorMessage);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new AssistantApiError(0, "No response body for stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  try {
    let reading = true;
    let terminated = false;
    while (reading && !terminated) {
      const { done, value } = await reader.read();
      if (done) {
        reading = false;
      } else {
        buffer += decoder.decode(value, { stream: true });
      }

      const events = parseSSEBuffer(buffer);
      buffer = events.remaining;

      for (const event of events.parsed) {
        if (dispatchSSEEvent(event, callbacks)) {
          terminated = true;
          break;
        }
      }
    }

    if (!terminated && buffer.trim()) {
      const finalEvents = parseSSEBuffer(buffer + "\n\n");
      for (const event of finalEvents.parsed) {
        if (dispatchSSEEvent(event, callbacks)) break;
      }
    }

    if (terminated) {
      await reader.cancel();
    }
  } finally {
    reader.releaseLock();
  }
}

/** Returns true if the event is terminal (done or error) and the reader should stop. */
function dispatchSSEEvent(event: ParsedSSEEvent, callbacks: StreamCallbacks): boolean {
  try {
    switch (event.type) {
      case "token": {
        const token: unknown = JSON.parse(event.data);
        callbacks.onToken(token as string);
        break;
      }
      case "metadata":
        break;
      case "done": {
        const parsed: unknown = JSON.parse(event.data);
        callbacks.onDone(parsed as AssistantChatResponse);
        break;
      }
      case "error": {
        const errorData: unknown = JSON.parse(event.data);
        const errObj = errorData as { message?: string };
        callbacks.onError(new AssistantApiError(502, errObj.message ?? "Stream error"));
        break;
      }
    }
  } catch {
    callbacks.onError(new AssistantApiError(502, `Failed to parse SSE event: ${event.type}`));
    return true;
  }
  return event.type === "done" || event.type === "error";
}

interface ParsedSSEEvent {
  type: string;
  data: string;
}

interface SSEParseResult {
  parsed: ParsedSSEEvent[];
  remaining: string;
}

export function parseSSEBuffer(buffer: string): SSEParseResult {
  const parsed: ParsedSSEEvent[] = [];
  const normalized = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const blocks = normalized.split("\n\n");
  const remaining = blocks.pop() ?? "";

  for (const block of blocks) {
    if (!block) continue;

    let eventType = "";
    const dataLines: string[] = [];

    for (const line of block.split("\n")) {
      if (line.startsWith("event:")) {
        eventType = line.slice(6).trimStart();
      } else if (line.startsWith("data:")) {
        dataLines.push(line.slice(5).trimStart());
      }
    }

    const eventData = dataLines.join("\n");

    if (eventType) {
      parsed.push({ type: eventType, data: eventData });
    }
  }

  return { parsed, remaining };
}
